import React, { FC, useEffect, useState } from 'react';
import { isAndroid, isIOS, isMobile, isTablet } from 'react-device-detect';
import { useNavigate } from 'react-router';
import { matchIsMobileDevice } from 'style/styled-components/cssMediaQueries';
import { Card } from 'components/Cards/Card/Card';
import { CardVariant } from 'components/Cards/Card/cardVariants';
import { Broadcast, ContinueWatchingItems } from '../../../types/Asset';
import { getContinueWatchingURL, getContinueWatchingPlayerURL } from '../../../utils/fnUrl';
import { useDetailLocationParams } from '../../../hooks/useDetailLocationParams';
import { useAuth } from '../../../providers/useAuth/AuthContext';
import { StandardCover } from '../../../types/Cover';
import { CardTrackingData } from '../../../types/Tracking';
import { CardContext } from '../../../types/Common';
import { ProgressIndicator } from '../../ProgressIndicator/ProgressIndicator';
import { useChannels } from '../../../providers/useChannels/useChannels';
import { useApp } from '../../../providers/useApp/AppContext';
import { buildEpisodeTitle } from '../../../utils/fnData';
import { getBookmark } from '../../../utils/playbackPosition';

interface ContinueWatchingCardPresenterProps {
    asset: ContinueWatchingItems;
    trackingData: CardTrackingData;
    cardContext: CardContext;
    onClick?: (event: React.MouseEvent<any>, data: Broadcast) => void;
}

export const ContinueWatchingCardPresenter: FC<ContinueWatchingCardPresenterProps> = ({ asset, cardContext, trackingData, onClick }) => {
    const { duration } = asset;
    const navigate = useNavigate();
    const locationParamsPath = useDetailLocationParams();
    const { getChannelById } = useChannels();
    const channel = asset.channel_id ? getChannelById(asset.channel_id) : null;
    const getPosition = (item: { id: string; episode_id?: string }) =>
        getBookmark(item.id, 'vod') ?? (item.episode_id && getBookmark(item.episode_id, 'listing'));
    const { isGuest } = useAuth();
    const { appLanguage } = useApp();

    const [isMobileDevice, setIsMobileDevice] = useState(() => matchIsMobileDevice());

    useEffect(() => {
        const mediaQuery = window.matchMedia('(pointer: coarse)');
        const handleChange = () => {
            setIsMobileDevice(matchIsMobileDevice());
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const onPlayClicked = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const playerUrl = getContinueWatchingPlayerURL(asset);
        if (playerUrl) {
            navigate(playerUrl);
        }
    };

    const bookmark = getPosition(asset);
    const progress = (bookmark * 100) / duration;

    const hasProgress = (): boolean => progress >= 0.02 && progress <= 95;

    const hasSeasonOrEpisode = asset.season_number != null || asset.episode_number != null;

    const cardTitle = hasSeasonOrEpisode
        ? asset.title
        : buildEpisodeTitle(
              asset.episode_title ? `${asset.title}: ${asset.episode_title}` : asset.title,
              asset.season_number,
              asset.episode_number
          );

    const cardMetaData = hasSeasonOrEpisode
        ? buildEpisodeTitle(asset.episode_title, asset.season_number, asset.episode_number)
        : asset.episode_title;

    const cover: StandardCover = {
        uuid: asset.id + (asset.channel_id || ''),
        id: asset.id,
        title: cardTitle,
        image: asset.image,
        landscapeImage: asset.image,
        channelImage: channel?.logo,
        metaData: cardMetaData,
        path: getContinueWatchingURL(asset, locationParamsPath, appLanguage),
        circle: false,
        landscape: true,
        assetType: asset.type,
        hideDescription: true,
        rawData: asset,
        renderPlayIcon: !isGuest && !isMobileDevice && (asset.isWatchable ?? false),
        progressBar: <ProgressIndicator percentage={hasProgress() && !isGuest ? progress : 0} />,
        defaultRenderSecondLine: isAndroid || isIOS || isTablet || isMobile,
        onPlayIconClicked: onPlayClicked,
        onClick,
    };

    if (cardContext === CardContext.STRIPE) {
        return <Card variant={CardVariant.LANDSCAPE} context={cardContext} asset={cover} />;
    }

    return <Card variant={CardVariant.LANDSCAPE} context={cardContext} asset={cover} {...trackingData} />;
};
